import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { initSupabase } from "@/shared/db";
import { initGroq } from "@/shared/ai";
import { errorTracker } from "./utils/errorTracker";
import { PostHogProvider } from "posthog-js/react";
import App from "./App";
import "./index.css";

// Apply theme before first paint (avoids flash)
(function initTheme() {
  const saved = localStorage.getItem("theme") || "light";
  const effective =
    saved === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : saved;
  document.documentElement.classList.toggle("dark", effective === "dark");
})();

// Initialize Supabase with environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    "Missing Supabase environment variables. Please check your .env file."
  );
}

initSupabase(supabaseUrl, supabaseAnonKey);

// Initialize Groq (optional - for faster document generation)
const groqApiKey = import.meta.env.VITE_GROQ_API_KEY;

if (groqApiKey) {
  try {
    initGroq(groqApiKey);
  } catch (error) {
    console.error("Groq initialization failed:", error);
  }
}

// Initialize error tracking
errorTracker.init();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
      options={{
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        defaults: "2025-05-24",
        capture_exceptions: true, // This enables capturing exceptions using Error Tracking, set to false if you don't want this
        debug: import.meta.env.VITE_APP_ENV === "development",
      }}
    >
      <App />
    </PostHogProvider>
  </StrictMode>
);
